import { parseUserAgent, DeviceInfo } from './DeviceInfo';

export interface SensorDetectionResult {
  isMobile: boolean;
  confidence: number; // 0-1 scale
  detectedFeatures: {
    // Motion sensors
    hasAccelerometer: boolean;
    hasGyroscope: boolean;
    hasOrientationSensor: boolean;

    // Touch capabilities
    hasTouchScreen: boolean;
    maxTouchPoints: number;

    // Screen characteristics
    screenSize: {
      width: number;
      height: number;
      diagonal: number; // inches
      pixelRatio: number;
    };

    // Device capabilities
    hasVibration: boolean;
    hasBattery: boolean;
    hasGeolocation: boolean;
    hasCamera: boolean;

    // Network
    connectionType?: string;

    // Orientation
    canChangeOrientation: boolean;
    currentOrientation: number;

    // Input methods
    hasKeyboard: boolean;
    hasMouse: boolean;
  };

  deviceType: 'mobile' | 'tablet' | 'desktop' | 'unknown';
  reasons: string[]; // Why this classification was chosen
}

class SensorMobileDetector {
  async detectMobileDevice(): Promise<SensorDetectionResult> {
    const features = await this.detectAllFeatures();
    const analysis = this.analyzeFeatures(features);

    return {
      isMobile: analysis.isMobile,
      confidence: analysis.confidence,
      detectedFeatures: features,
      deviceType: analysis.deviceType,
      reasons: analysis.reasons,
    };
  }

  private async detectAllFeatures() {
    // Run all detection methods in parallel
    const [motionSensors, touchCapabilities, screenInfo, deviceCapabilities, networkInfo, orientationInfo, inputMethods] = await Promise.all([
      this.detectMotionSensors(),
      this.detectTouchCapabilities(),
      this.getScreenInformation(),
      this.detectDeviceCapabilities(),
      this.detectNetworkType(),
      this.detectOrientationCapabilities(),
      this.detectInputMethods(),
    ]);

    return {
      ...motionSensors,
      ...touchCapabilities,
      screenSize: screenInfo,
      ...deviceCapabilities,
      ...networkInfo,
      ...orientationInfo,
      ...inputMethods,
    };
  }

  // Motion Sensors Detection
  private async detectMotionSensors(): Promise<{
    hasAccelerometer: boolean;
    hasGyroscope: boolean;
    hasOrientationSensor: boolean;
  }> {
    let hasAccelerometer = false;
    let hasGyroscope = false;
    let hasOrientationSensor = false;

    // Test DeviceMotionEvent (accelerometer/gyroscope)
    if ('DeviceMotionEvent' in window) {
      try {
        // Modern approach with permissions
        if (typeof (DeviceMotionEvent as any).requestPermission === 'function') {
          const permission = await (DeviceMotionEvent as any).requestPermission();
          if (permission === 'granted') {
            hasAccelerometer = await this.testMotionEvent();
          }
        } else {
          // Legacy approach - just test if events fire
          hasAccelerometer = await this.testMotionEvent();
        }
      } catch (error) {
        console.log('DeviceMotionEvent test failed:', error);
      }
    }

    // Test DeviceOrientationEvent
    if ('DeviceOrientationEvent' in window) {
      try {
        if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
          const permission = await (DeviceOrientationEvent as any).requestPermission();
          if (permission === 'granted') {
            hasOrientationSensor = await this.testOrientationEvent();
          }
        } else {
          hasOrientationSensor = await this.testOrientationEvent();
        }
      } catch (error) {
        console.log('DeviceOrientationEvent test failed:', error);
      }
    }

    // Gyroscope is typically available if accelerometer is
    hasGyroscope = hasAccelerometer;

    return { hasAccelerometer, hasGyroscope, hasOrientationSensor };
  }

  private testMotionEvent(): Promise<boolean> {
    return new Promise(resolve => {
      let hasMotion = false;

      const handleMotion = (event: DeviceMotionEvent) => {
        if (event.acceleration || event.accelerationIncludingGravity || event.rotationRate) {
          hasMotion = true;
        }
      };

      window.addEventListener('devicemotion', handleMotion);

      // Test for 2 seconds
      setTimeout(() => {
        window.removeEventListener('devicemotion', handleMotion);
        resolve(hasMotion);
      }, 2000);
    });
  }

  private testOrientationEvent(): Promise<boolean> {
    return new Promise(resolve => {
      let hasOrientation = false;

      const handleOrientation = (event: DeviceOrientationEvent) => {
        if (event.alpha !== null || event.beta !== null || event.gamma !== null) {
          hasOrientation = true;
        }
      };

      window.addEventListener('deviceorientation', handleOrientation);

      setTimeout(() => {
        window.removeEventListener('deviceorientation', handleOrientation);
        resolve(hasOrientation);
      }, 1000);
    });
  }

  // Touch Capabilities
  private async detectTouchCapabilities(): Promise<{
    hasTouchScreen: boolean;
    maxTouchPoints: number;
  }> {
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const maxTouchPoints = navigator.maxTouchPoints || 0;

    return { hasTouchScreen, maxTouchPoints };
  }

  // Screen Information
  private async getScreenInformation() {
    const width = screen.width;
    const height = screen.height;
    const pixelRatio = window.devicePixelRatio || 1;

    // Calculate diagonal in inches (assuming standard DPI)
    const diagonalPixels = Math.sqrt(width * width + height * height);
    const dpi = pixelRatio * 96; // Rough estimate
    const diagonal = diagonalPixels / dpi;

    return {
      width,
      height,
      diagonal,
      pixelRatio,
    };
  }

  // Device Capabilities
  private async detectDeviceCapabilities(): Promise<{
    hasVibration: boolean;
    hasBattery: boolean;
    hasGeolocation: boolean;
    hasCamera: boolean;
  }> {
    const hasVibration = 'vibrate' in navigator;
    const hasGeolocation = 'geolocation' in navigator;

    // Battery API (deprecated but still useful for detection)
    let hasBattery = false;
    try {
      if ('getBattery' in navigator) {
        await (navigator as any).getBattery();
        hasBattery = true;
      } else if ('battery' in navigator) {
        hasBattery = true;
      }
    } catch (e) {
      // Battery API might be restricted
    }

    // Camera detection via getUserMedia
    let hasCamera = false;
    try {
      if ('mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        hasCamera = devices.some(device => device.kind === 'videoinput');
      }
    } catch (e) {
      // Permission denied or not available
      hasCamera = 'mediaDevices' in navigator;
    }

    return { hasVibration, hasBattery, hasGeolocation, hasCamera };
  }

  // Network Type Detection
  private async detectNetworkType(): Promise<{ connectionType?: string }> {
    let connectionType: string | undefined;

    // Network Information API
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;

    if (connection) {
      connectionType = connection.effectiveType || connection.type;
    }

    return { connectionType };
  }

  // Orientation Capabilities
  private async detectOrientationCapabilities(): Promise<{
    canChangeOrientation: boolean;
    currentOrientation: number;
  }> {
    const canChangeOrientation = 'orientation' in screen || 'orientation' in window;
    const currentOrientation = screen.orientation?.angle || (window as any).orientation || 0;

    return { canChangeOrientation, currentOrientation };
  }

  // Input Method Detection
  private async detectInputMethods(): Promise<{
    hasKeyboard: boolean;
    hasMouse: boolean;
  }> {
    // This is tricky - we'll use heuristics
    let hasKeyboard = false;
    let hasMouse = false;

    // Check for mouse events
    hasMouse = 'onmouseenter' in window;

    // Check for keyboard
    hasKeyboard = 'onkeydown' in window;

    // More sophisticated detection via event listeners
    try {
      const result = await this.detectActualInputUsage();
      hasKeyboard = result.hasKeyboard;
      hasMouse = result.hasMouse;
    } catch (e) {
      // Fallback to basic detection
    }

    return { hasKeyboard, hasMouse };
  }

  private detectActualInputUsage(): Promise<{ hasKeyboard: boolean; hasMouse: boolean }> {
    return new Promise(resolve => {
      let mouseDetected = false;
      let keyboardDetected = false;

      const mouseHandler = () => {
        mouseDetected = true;
      };
      const keyHandler = () => {
        keyboardDetected = true;
      };

      document.addEventListener('mousemove', mouseHandler);
      document.addEventListener('keydown', keyHandler);

      setTimeout(() => {
        document.removeEventListener('mousemove', mouseHandler);
        document.removeEventListener('keydown', keyHandler);
        resolve({ hasMouse: mouseDetected, hasKeyboard: keyboardDetected });
      }, 3000);
    });
  }

  // Analysis Logic
  private analyzeFeatures(features: any): {
    isMobile: boolean;
    confidence: number;
    deviceType: 'mobile' | 'tablet' | 'desktop' | 'unknown';
    reasons: string[];
  } {
    const reasons: string[] = [];
    let mobileScore = 0;
    let tabletScore = 0;
    let desktopScore = 0;

    // Motion sensors (strong mobile indicator)
    if (features.hasAccelerometer || features.hasGyroscope) {
      mobileScore += 0.3;
      tabletScore += 0.2;
      reasons.push('Has motion sensors');
    }

    if (features.hasOrientationSensor) {
      mobileScore += 0.2;
      tabletScore += 0.15;
      reasons.push('Has orientation sensor');
    }

    // Touch screen
    if (features.hasTouchScreen) {
      mobileScore += 0.25;
      tabletScore += 0.3;
      reasons.push(`Touch screen with ${features.maxTouchPoints} points`);
    } else {
      desktopScore += 0.3;
      reasons.push('No touch screen detected');
    }

    // Screen size analysis
    const { diagonal, pixelRatio } = features.screenSize;
    if (diagonal < 7) {
      mobileScore += 0.4;
      reasons.push(`Small screen size: ${diagonal.toFixed(1)}" diagonal`);
    } else if (diagonal < 11) {
      tabletScore += 0.4;
      reasons.push(`Medium screen size: ${diagonal.toFixed(1)}" diagonal`);
    } else {
      desktopScore += 0.3;
      reasons.push(`Large screen size: ${diagonal.toFixed(1)}" diagonal`);
    }

    // High pixel ratio often indicates mobile
    if (pixelRatio > 2) {
      mobileScore += 0.15;
      tabletScore += 0.1;
      reasons.push(`High pixel ratio: ${pixelRatio}x`);
    }

    // Device capabilities
    if (features.hasVibration) {
      mobileScore += 0.2;
      tabletScore += 0.1;
      reasons.push('Has vibration capability');
    }

    if (features.hasBattery) {
      mobileScore += 0.15;
      tabletScore += 0.15;
      reasons.push('Has battery API');
    }

    // Orientation capability
    if (features.canChangeOrientation) {
      mobileScore += 0.2;
      tabletScore += 0.25;
      reasons.push('Can change orientation');
    }

    // Network type
    if (features.connectionType) {
      if (['slow-2g', '2g', '3g'].includes(features.connectionType)) {
        mobileScore += 0.15;
        reasons.push(`Mobile network: ${features.connectionType}`);
      } else if (features.connectionType === '4g') {
        mobileScore += 0.1;
        tabletScore += 0.1;
        reasons.push(`4G network detected`);
      }
    }

    // Input methods (desktop indicators)
    if (!features.hasTouchScreen && features.hasMouse) {
      desktopScore += 0.2;
      reasons.push('Mouse input detected');
    }

    // Determine final classification
    const maxScore = Math.max(mobileScore, tabletScore, desktopScore);
    let deviceType: 'mobile' | 'tablet' | 'desktop' | 'unknown' = 'unknown';
    let isMobile = false;

    if (maxScore === mobileScore && mobileScore > 0.5) {
      deviceType = 'mobile';
      isMobile = true;
    } else if (maxScore === tabletScore && tabletScore > 0.5) {
      deviceType = 'tablet';
      isMobile = true; // Tablets are considered mobile for most purposes
    } else if (maxScore === desktopScore && desktopScore > 0.4) {
      deviceType = 'desktop';
      isMobile = false;
    }

    const confidence = maxScore;

    return { isMobile, confidence, deviceType, reasons };
  }
}

// Enhanced device detection combining sensors and user agent
export async function detectDeviceComprehensively(): Promise<{
  sensorResult: SensorDetectionResult;
  userAgentInfo: DeviceInfo;
  finalClassification: {
    isMobile: boolean;
    deviceType: string;
    confidence: number;
    method: 'sensors' | 'useragent' | 'combined';
  };
}> {
  const sensorDetector = new SensorMobileDetector();

  try {
    // Get sensor-based detection
    const sensorResult = await sensorDetector.detectMobileDevice();

    // Get user agent info (from previous artifact)
    const userAgentInfo = parseUserAgent();

    // Combine both methods for final classification
    let finalClassification;

    if (sensorResult.confidence > 0.7) {
      // High confidence from sensors
      finalClassification = {
        isMobile: sensorResult.isMobile,
        deviceType: sensorResult.deviceType,
        confidence: sensorResult.confidence,
        method: 'sensors' as const,
      };
    } else if (sensorResult.confidence > 0.3) {
      // Medium confidence - combine with user agent
      const combinedMobile = sensorResult.isMobile || userAgentInfo.device.isMobile;
      finalClassification = {
        isMobile: combinedMobile,
        deviceType: combinedMobile ? 'mobile' : 'desktop',
        confidence: (sensorResult.confidence + 0.5) / 2,
        method: 'combined' as const,
      };
    } else {
      // Low sensor confidence - rely on user agent
      finalClassification = {
        isMobile: userAgentInfo.device.isMobile,
        deviceType: userAgentInfo.device.type,
        confidence: 0.6,
        method: 'useragent' as const,
      };
    }

    return {
      sensorResult,
      userAgentInfo,
      finalClassification,
    };
  } catch (error) {
    console.error('Sensor detection failed:', error);

    // Fallback to user agent only
    const userAgentInfo = parseUserAgent();
    return {
      sensorResult: {
        isMobile: userAgentInfo.device.isMobile,
        confidence: 0.5,
        detectedFeatures: {} as any,
        deviceType: userAgentInfo.device.type as any,
        reasons: ['Sensor detection failed, using user agent'],
      },
      userAgentInfo,
      finalClassification: {
        isMobile: userAgentInfo.device.isMobile,
        deviceType: userAgentInfo.device.type,
        confidence: 0.5,
        method: 'useragent',
      },
    };
  }
}

// Utility class for easy integration
export class SmartDeviceDetector {
  private static instance: SmartDeviceDetector;
  private detectionResult: {
    sensorResult: SensorDetectionResult;
    userAgentInfo: DeviceInfo;
    finalClassification: {
      isMobile: boolean;
      deviceType: string;
      confidence: number;
      method: 'sensors' | 'useragent' | 'combined';
    };
  } | null = null;
  private isDetecting = false;

  static getInstance(): SmartDeviceDetector {
    if (!this.instance) {
      this.instance = new SmartDeviceDetector();
    }
    return this.instance;
  }

  async isMobileDevice(): Promise<boolean> {
    const result = await this.getDetectionResult();
    return result.finalClassification.isMobile;
  }

  async getDeviceType(): Promise<string> {
    const result = await this.getDetectionResult();
    return result.finalClassification.deviceType;
  }

  async getConfidence(): Promise<number> {
    const result = await this.getDetectionResult();
    return result.finalClassification.confidence;
  }

  public async getDetectionResult() {
    if (!this.detectionResult && !this.isDetecting) {
      this.isDetecting = true;
      try {
        this.detectionResult = await detectDeviceComprehensively();
      } finally {
        this.isDetecting = false;
      }
    }

    // Wait for detection to complete
    while (this.isDetecting) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return this.detectionResult!;
  }
}
